/**
 * location.js — Condivisione posizione SICURA e consensuale.
 *
 * Principio: una posizione condivisa NON e' tracciamento, e' CONTENUTO che
 * l'utente sceglie di mandare. Viaggia dentro un normale messaggio cifrato
 * E2E: il server vede solo byte opachi e NON sa nemmeno che si tratta di una
 * posizione (nessun campo "location" lato server). Effimera come ogni
 * messaggio (TTL scelto dal mittente).
 *
 * L'utente sceglie:
 *  - PRECISIONE: 'exact' (GPS pieno) o 'approx' (arrotondata a ~1 km).
 *  - DURATA: tramite il TTL del messaggio (selettore esistente in chat).
 *
 * Niente mappe incorporate (traccerebbero chi guarda): mostriamo le coordinate
 * + un link a OpenStreetMap che l'utente apre se vuole.
 */

// Tag riconosciuto SOLO dopo la decifratura, lato client. Caratteri di
// controllo per non collidere con testo reale digitato dall'utente.
const LOC_TAG = 'nullum.loc.v1';

/**
 * Costruisce il payload-stringa da cifrare come testo del messaggio.
 * @param {{lat:number,lng:number,acc:?number,precision:'exact'|'approx'}} p
 * @returns {string}
 */
export function buildLocationPayload({ lat, lng, acc, precision }) {
  const approx = precision === 'approx';
  // approx: 2 decimali ≈ 1.1 km di griglia; exact: 6 decimali ≈ 0.1 m.
  const round = (v) => (approx ? Math.round(v * 100) / 100 : Math.round(v * 1e6) / 1e6);
  const obj = {
    lat: round(lat),
    lng: round(lng),
    acc: approx ? null : (acc != null ? Math.round(acc) : null),
    at: Date.now(),
    p: approx ? 'approx' : 'exact',
  };
  return LOC_TAG + JSON.stringify(obj);
}

/**
 * Riconosce e interpreta un payload-posizione DOPO la decifratura.
 * @param {string} text  plaintext del messaggio decifrato
 * @returns {?{lat:number,lng:number,acc:?number,at:?number,precision:string}}
 */
export function parseLocation(text) {
  if (typeof text !== 'string' || !text.startsWith(LOC_TAG)) return null;
  try {
    const o = JSON.parse(text.slice(LOC_TAG.length));
    if (typeof o.lat !== 'number' || typeof o.lng !== 'number') return null;
    return {
      lat: o.lat,
      lng: o.lng,
      acc: typeof o.acc === 'number' ? o.acc : null,
      at: typeof o.at === 'number' ? o.at : null,
      precision: o.p === 'approx' ? 'approx' : 'exact',
    };
  } catch {
    return null;
  }
}

/** Link OpenStreetMap (privacy-friendly, universale) per un pin. */
export function osmLink(lat, lng) {
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=16/${lat}/${lng}`;
}

/**
 * Lettura UNA-TANTUM della posizione corrente. Richiede HTTPS + permesso
 * esplicito dell'utente (il browser mostra il prompt). Mai in background.
 * @returns {Promise<{lat:number,lng:number,acc:?number}>}
 */
export function getCurrentPositionOnce(opts = {}) {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      reject(new Error('GEO_UNSUPPORTED'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        acc: pos.coords.accuracy ?? null,
      }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0, ...opts },
    );
  });
}
